/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { LogProvider } from './Interfaces';
import { IMessageLossIndicator, ITrouterRequest, UserActivityEventReason } from './InternalInterfaces';
import { Logger } from './Logger';
import { ReconnectReason } from './TrouterConnection';
import { UserActivityObject } from './TrouterManager';

/**
 * After this count of 'soft' (5XX, no response) connection errors, FSM will start a new allocation.
 * Doesn't apply for 'hard' errors like 4XX, in this case FSM will start a new allocation immediately.
 */
const MAXIMUM_CONNECTION_ERRORS_COUNT = 2;

export interface IWorker {
    getToken(useCache: boolean, backoff: boolean);

    startConnectionTimer();
    stopConnectionTimer();
    startPingTimer();
    stopPingTimer();

    /** Returns true if config mandates to not register connection URL with EDF Registrar */
    shouldSkipRegistration();
    /** Returns true if user specified a custom TTL for registration */
    hasCustomRegisterTtl();
    startRegisterTimer();
    stopRegisterTimer();

    startSocketIo();
    stopSocketIo();
    isIncallMode();
    restartIncallModeTimer();
    enterIncallMode();
    exitIncallMode();

    dispatchConnected();
    dispatchRegistered();
    dispatchUnregistered();
    dispatchDownstreamRequest(request: ITrouterRequest);
    /** Start reconnecting due to socket error */
    dispatchReconnecting();
    /** Reconnection is requested by server (current connection is still valid) */
    dispatchReconnectIsRequired(useConnectParamsFromCache: boolean, reason: ReconnectReason);
    dispatchDisconnected();
    dispatchTrouterMessageLost(flowTags: IMessageLossIndicator[]);

    resetAllocationConnectionParams();
    sendAllocateRequest(token: string);
    sendPingRequest();

    sendUserActivityState(reason: UserActivityEventReason, connected: boolean, correlationVector?: string);

    sendRegisterRequest();
    sendUnregisterRequest();

    /** Reset planned token request (if any) and backoff interval */
    resetTokenBackoff();
    /** Reset planned registration requests (if any) */
    resetRegisterBackoff();
    /** Cancel all pending downstream events related timers. */
    clearTimers();
    sendDisconnectTelemetryEvent(error?: string);
}

export enum State {
    Initial,
    /** waiting for token from User */
    RetrievingToken,
    /** waiting for allocation response from service */
    Allocating,
    /**
     * waiting for Socket.IO handshake to be finished. After that Socket.IO will
     * start to try transports and send 'connecting' event
     */
    Handshaking,
    /**
     * waiting for 'connected' event - socket.io is trying different transports.
     * onSocketError event should be ignored, as it's expected event. So, only ConnectFailed or
     * connection timeout meas failed connection
     */
    Connecting,
    /** ready to work */
    Connected,
    /** waiting for token from User to send 'unregister' request */
    UnregisteringRetrievingToken,
    /** waiting for succesful 'unregister' response */
    Unregistering,
    /**
     * Unrecoverable state
     * Trouter cannot start once in this state
     */
    TerminalError
}

enum ConnectedSubstate {
    Initial,
    /** waiting for token from User */
    RetrievingToken,
    /** waiting for succesful 'register' response */
    Registering,
    /** Registration succeed */
    Registered,
    /** Registration is skipped */
    NotRegistered
}

/**
 * State machine for trouter
 */
export class TrouterFsm {
    private logger: Logger;
    private state: State = State.Initial;
    private connectedSubstate: ConnectedSubstate;
    private connectionErrors = 0;
    private autoReconnect = true;

    constructor(logfunc: LogProvider, private worker: IWorker, private incallModeEnabled: boolean) {
        this.logger = new Logger('ConnectionFsm', logfunc);
        this.connectedSubstate = ConnectedSubstate.Initial;
    }

    /** Returns current state */
    public getState(): State {
        return this.state;
    }

    public isActive(): boolean {
        return this.state === State.Allocating
            || this.state === State.Connected
            || this.state === State.Handshaking
            || this.state === State.Connecting
            || this.state === State.RetrievingToken;
    }

    //////////// Events ////////////
    /**
     * Start trouter
     */
    public start(): boolean {
        if (this.state === State.Initial) {
            this.setState(State.RetrievingToken);
            this.worker.getToken(true, false);

            return true;
        } else {
            this.showIgnored('start');

            return false;
        }
    }

    /**
     * Stop trouter
     * @param dontUnregister    if true, FSM will not unregister. It's needed in case of two simultaneous
     *                          connections, to replace first connection with second - registrar just updates
     *                          the existing record for the user, by registrationId (see their API docs),
     *                          that id does not change during the lifetime of a TrouterService instance.
     */
    public stop(dontUnregister: boolean) {
        if (dontUnregister) {
            this.connectedSubstate = ConnectedSubstate.Initial;
        }

        if (this.worker.isIncallMode()) {
            this.worker.exitIncallMode();
        }

        this.worker.resetTokenBackoff();
        this.worker.resetRegisterBackoff();
        this.worker.stopConnectionTimer();
        this.worker.stopPingTimer();
        this.worker.clearTimers();
        this.worker.stopRegisterTimer();

        if (this.state === State.Connecting
            || this.state === State.Handshaking
            || this.state === State.Connected
        ) {
            this.worker.stopSocketIo();
            if (this.state === State.Connected) {
                this.worker.sendDisconnectTelemetryEvent('connection stopped');
            }
        }

        // Stop can happen in any state - the only difference is if registration exists or not.
        // It can be also called a second time, just to stop a stuck unregistration.
        if (this.isRegistered() && this.state !== State.UnregisteringRetrievingToken && this.state !== State.Unregistering) {
            this.setState(State.UnregisteringRetrievingToken);
            this.worker.getToken(true, false);
        } else {
            this.setState(State.Initial);
            this.worker.dispatchDisconnected();
        }
    }

    public onTokenReceived(token: string) {
        if (this.state === State.RetrievingToken) {
            this.setState(State.Allocating);
            this.worker.startConnectionTimer();
            this.worker.sendAllocateRequest(token);
        } else if (this.state === State.Connected && this.connectedSubstate === ConnectedSubstate.RetrievingToken) {
            this.connectedSubstate = ConnectedSubstate.Registering;
            this.worker.sendRegisterRequest();
        } else if (this.state === State.UnregisteringRetrievingToken) {
            this.setState(State.Unregistering);
            this.worker.sendUnregisterRequest();
        } else {
            this.showIgnored('onTokenReceived');
        }
    }

    /**
     * if disconnectDetected - need to send ping to check if connection is really dead,
     * otherwise - network is available
     */
    public checkConnection(disconnectDetected: boolean) {
        if (disconnectDetected) {
            this.onPingInterval();
        }
    }

    /** Allocation request succeed with 200 */
    public onAllocationSucceed(): boolean {
        if (this.state === State.Allocating) {
            this.setState(State.Handshaking);
            if (this.connectedSubstate === ConnectedSubstate.Registered) {
                this.worker.dispatchUnregistered();
            }
            this.connectedSubstate = ConnectedSubstate.Initial;
            this.worker.startSocketIo();

            return true;
        } else {
            this.showIgnored('onAllocationSucceed');

            return false;
        }
    }

    /** Allocation request failed */
    public onAllocationFailed(unauthorized: boolean) {
        if (this.state === State.Allocating) {
            this.setState(State.RetrievingToken);
            this.worker.stopConnectionTimer();
            this.worker.getToken(!unauthorized, true);
        } else {
            this.showIgnored('onAllocationFailed');
        }
    }

    /** Connection didn't succeed in given timeout */
    public onConnectingTimeout() {
        // implementation note: this also called from onConnectingFailed
        if (this.state === State.Allocating
            || this.state === State.Connecting
            || this.state === State.Handshaking
        ) {
            this.setState(State.RetrievingToken);
            this.resetAllocationIfNecessary(undefined);
            this.worker.stopSocketIo();
            this.worker.getToken(true, false);
        } else {
            this.showIgnored('onConnectingTimeout');
        }
    }

    /** socket.io handshake succeed and socket.io is trying next transport */
    public onConnecting() {
        if (this.state === State.Handshaking) {
            this.setState(State.Connecting);
        } else {
            this.showIgnored('onConnecting');
        }
    }

    /** Socket.io connection wasn't established */
    public onConnectingFailed() {
        // This callback will be called when all transports failed to connect, i.e. socket.io can't proceed
        if (this.state === State.Connecting) {
            this.onConnectingTimeout();
        } else if (this.state === State.Handshaking) {
            // It should never happen, because socket.io should try at least one transport
            this.logger.error('Unexpected error in Socket.io - no valid transports');
            this.onConnectingTimeout();
        } else {
            this.showIgnored('onConnectingFailed');
        }
    }

    /** Socket.io error (or disconnect) */
    public onSocketDisconnect(disconnectReason?: string) {
        if (this.state === State.Handshaking || this.state === State.Connected) {
            this.worker.stopSocketIo();
            this.worker.stopPingTimer();
            this.worker.clearTimers();
            this.worker.stopConnectionTimer();
            if (this.state === State.Connected) {
                this.worker.sendDisconnectTelemetryEvent(disconnectReason);
            } else {
                this.worker.resetAllocationConnectionParams();
            }
            if (this.autoReconnect) {
                this.setState(State.RetrievingToken);
                this.worker.resetRegisterBackoff();
                this.worker.dispatchReconnecting();
                this.worker.getToken(true, false);
            } else {
                this.logger.debug('Socket error/disconnect occurred and automatic reconnect is disabled. Connection will stop.');
                this.stop(true);
            }
        } else {
            // Disconnect can be called because of an error.
            // Such error can happen in Connecting state, which means that
            // websocket transport failed to establish connection
            this.showIgnored('onSocketDisconnect');
        }
    }

    /** Received 'trouter.connected' event from service */
    public onTrouterConnected() {
        if (this.state === State.Connecting) {
            this.setState(State.Connected);
            this.worker.resetTokenBackoff();
            this.worker.stopConnectionTimer();
            this.worker.sendUserActivityState(UserActivityEventReason.Connected, true);
            this.worker.startPingTimer();
            this.worker.dispatchConnected();
            this.connectionErrors = 0;
            if (this.worker.shouldSkipRegistration()) {
                this.connectedSubstate = ConnectedSubstate.NotRegistered;
                this.worker.dispatchRegistered();
            } else {
                this.connectedSubstate = ConnectedSubstate.RetrievingToken;
                this.worker.getToken(true, false);
            }
        } else {
            this.showIgnored('onTrouterConnected');
        }
    }

    /**
     * Received 'trouter.reconnect' event from service, or URL in configuration has changed,
     * need to establish a new connection
     */
    public onReconnectRequired(useConnectParamsFromCache: boolean, reason: ReconnectReason) {
        this.worker.dispatchReconnectIsRequired(useConnectParamsFromCache, reason);
    }

    public disableAutoReconnect() {
        this.autoReconnect = false;
    }

    /** Request from downstream is received */
    public onDownstreamRequest(request: ITrouterRequest) {
        if (this.state === State.Connected) {
            this.switchToIncallModeIfEnabled();
            this.worker.dispatchDownstreamRequest(request);
        } else {
            this.showIgnored('onDownstreamRequest');
        }
    }

    public onTrouterMessageLost(messageLossIndicators: IMessageLossIndicator[]) {
        if (this.state === State.Connected) {
            this.worker.dispatchTrouterMessageLost(messageLossIndicators);
        } else {
            this.showIgnored('onTrouterMessageLost');
        }
    }

    /** Timer for ping request fired */
    public onPingInterval() {
        if (this.state === State.Connected) {
            this.worker.sendPingRequest();
        } else {
            this.showIgnored('onPingInterval');
        }
    }

    /** No pong response from service within timeout */
    public onPingResponseTimeout() {
        this.onMissedResponse('onPingResponseTimeout');
    }

    /** Pong response */
    public onPingResponse() {
        if (this.state === State.Connected) {
            // do nothing
        } else {
            this.showIgnored('onPingResponse');
        }
    }

    public onRegistrationFailed(unauthorized: boolean) {
        if (this.state === State.Connected
            && this.connectedSubstate === ConnectedSubstate.Registering
        ) {
            this.connectedSubstate = ConnectedSubstate.RetrievingToken;
            this.worker.getToken(!unauthorized, true);
        } else {
            this.showIgnored('onRegistrationFailed');
        }
    }

    public onRegistrationSucceed(isRegistrationTtlLowerThanKnownAllocationTtl: boolean) {
        if (this.state === State.Connected) {
            if (this.connectedSubstate === ConnectedSubstate.Registering) {
                this.connectedSubstate = ConnectedSubstate.Registered;
                this.worker.dispatchRegistered();
            }
            // If registration ttl is lower than known allocation ttl we need to start re-registration.
            // Otherwise, the allocation will expire with the registration, client will reconnect with a new
            // allocation, a new connection id. which in turn will trigger a new registration started.
            // i.e. no need to re-register the current expiring one.
            if (isRegistrationTtlLowerThanKnownAllocationTtl) {
                this.worker.startRegisterTimer();
            }
        } else {
            this.showIgnored('onRegistrationSucceed');
        }
    }

    /** Registration TTL is about to expire */
    public onRegistrationTimeout() {
        if (this.state === State.Connected
            && this.connectedSubstate !== ConnectedSubstate.NotRegistered
        ) {
            this.connectedSubstate = ConnectedSubstate.RetrievingToken;
            this.worker.getToken(true, false);
        } else {
            this.showIgnored('onRegistrationTimeout');
        }
    }

    public onUnregistrationFailed(unauthorized: boolean) {
        if (this.state === State.Unregistering) {
            this.setState(State.UnregisteringRetrievingToken);
            this.worker.getToken(!unauthorized, !unauthorized);
        } else {
            this.showIgnored('onUnregistrationFailed');
        }
    }

    public onUnregistrationSucceed() {
        if (this.state === State.Unregistering) {
            this.setState(State.Initial);
            this.worker.dispatchUnregistered();
            this.worker.dispatchDisconnected();
        } else {
            this.showIgnored('onUnregistrationSucceed');
        }
    }

    public onIncallModeTimer() {
        this.worker.exitIncallMode();

        if (this.state === State.Connected) {
            this.worker.stopPingTimer();
            this.worker.startPingTimer();
        } else {
            this.showIgnored('onIncallModeTimer');
        }
    }

    public onSetUserActivityState(userActivityObject: UserActivityObject, changed: boolean) {
        if (changed) {
            this.logger.info(`Changing user activity state to '${userActivityObject.toEventJSON()}'`);
            this.worker.sendUserActivityState(UserActivityEventReason.Modified, this.state === State.Connected);
        } else {
            this.logger.debug(`Not changing the same user activity state '${userActivityObject.toEventJSON()}'`);
        }
    }

    public onActivityStateResponseTimeout() {
        this.onMissedResponse('onActivityStateResponseTimeout');
    }


    public forceReconnect(reason: string) {
        if (this.state === State.Connected) {
            this.worker.sendDisconnectTelemetryEvent(reason);
        }
        this.setState(State.RetrievingToken);
        this.worker.resetTokenBackoff();
        this.worker.stopConnectionTimer();
        this.worker.resetRegisterBackoff();
        this.worker.stopPingTimer();
        this.worker.clearTimers();
        this.worker.stopSocketIo();
        this.worker.dispatchReconnecting();
        this.worker.getToken(true, false);
    }

    public onTerminalError() {
        this.logger.error(`Cannot proceed, reached terminal state. Switching from state '${State[this.state]}' to ${State[State.TerminalError]}`);
        this.setState(State.TerminalError);
    }

    private onMissedResponse(onResponseHandlerName: string) {
        if (this.state === State.Connected) {
            this.setState(State.RetrievingToken);
            this.worker.resetRegisterBackoff();
            this.worker.stopPingTimer();
            this.worker.clearTimers();
            this.worker.stopSocketIo();
            this.worker.sendDisconnectTelemetryEvent(onResponseHandlerName);
            this.worker.dispatchReconnecting();
            this.worker.getToken(true, false);
        } else {
            this.showIgnored(onResponseHandlerName);
        }
    }

    private showIgnored(event: string) {
        this.logger.debug(`Ignoring event '${event}' in state '${State[this.state]}'`);
    }

    private setState(state: State) {
        this.logger.info(`Switching from state '${State[this.state]}' to state '${State[state]}'`);
        if (this.state === state) {
            this.logger.error(`Attempt to switching into the current state '${State[state]}'`);

            return;
        }
        this.state = state;
    }

    private resetAllocationIfNecessary(error?: string) {
        this.connectionErrors++;
        if (this.connectionErrors >= MAXIMUM_CONNECTION_ERRORS_COUNT  // Too many errors for this allocation
            || (error !== undefined && /^4\d\d /.test(error)) // Client error (like 401) - need to start with a new allocation
        ) {
            this.logger.error(`Connection error count is ${this.connectionErrors} - will reset allocation`);
            this.connectionErrors = 0;
        } else {
            this.logger.info(`Connection error count is ${this.connectionErrors} - will NOT reset allocation`);
        }
    }

    private isRegistered() {
        return this.connectedSubstate === ConnectedSubstate.Registered
            || this.connectedSubstate === ConnectedSubstate.Registering;
    }

    private switchToIncallModeIfEnabled() {
        if (this.incallModeEnabled) {
            if (!this.worker.isIncallMode()) {
                this.worker.enterIncallMode();
                this.worker.stopPingTimer();
                this.worker.startPingTimer();
            }
            this.worker.restartIncallModeTimer();
        }
    }
}



// WEBPACK FOOTER //
// ./lib_temp/TrouterFsm.ts